import { Button, useDisclosure } from '@chakra-ui/react'
import { FormProvider, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from "@hookform/resolvers/yup"
import _ from 'lodash'
import {
  FormSection,
  FormSectionBody,
  FormSectionDescription,
  FormSectionHeader,
  FormSectionTitle,
} from '../../components/FormSection'
import { MainContent } from '../../components/MainContent'
import { FormButtons } from '../FormButtons'
import { Save } from '../../icons'
import { ExtraInsuranceCestovko } from '../ExtraInsuranceCestovko'

import { insuranceParamsContext } from '../../layouts/Wizard'
import { useNavigate } from 'react-router-dom'
import { useState, useContext, useEffect } from 'react'
import { AxiosError } from 'axios'
import { InsuranceParamsTravelDto, TravelAddonsDto } from 'BrokerTsLib/src/dtos'
import { DiscountBase, StepBase } from 'BrokerTsLib/src/dtos/StepBase'
import { TravelAddonValidationSchema } from 'brokertslib/src/validation'
import ErrorSummary from '../../components/ErrorSummary'
import { useUpdateLastWizardStep, useSaveStep2 } from '../../hooks/useWizard'
import SubmitDialog from 'BrokerTsLib/src/components/SubmitDialog'
import { Discounts } from '../discounts/Discounts'
import { FormSpacer } from '../../components/FormSpacer'
import { formatDate } from 'BrokerTsLib/src/Utils/formatPrice'

export type Step2FormValues = {
  paramsId: string
  addons: TravelAddonsDto
} & StepBase & DiscountBase

export const schema = yup.object().shape(
  {
    addons: TravelAddonValidationSchema,
  }
)

export const transformInsuranceParamsToStep2FormValues = (dto: InsuranceParamsTravelDto, paramsId?: string) => {
  return {
    paramsId: paramsId,
    addons: { ...dto.addons }
  }
}

export const Step2Form = () => {
  const navigate = useNavigate()
  const { insuranceParams: insuranceParamsGeneric, insuranceParamsId: paramsId, refetchInsuranceParams } = useContext(insuranceParamsContext)!
  const insuranceParams = insuranceParamsGeneric as InsuranceParamsTravelDto

  const [isSubmittingCurrent, setIsSubmittingCurrent] = useState(false)
  const [isSubmittingNext, setIsSubmittingNext] = useState(false)

  const formValues = transformInsuranceParamsToStep2FormValues(insuranceParams, paramsId);

  const isContracted = !!insuranceParams.generalParams.contractNumber

  const methods = useForm<Step2FormValues>({
    defaultValues: formValues,
    resolver: yupResolver(schema) as any,
    mode: 'onBlur'
  })

  const {
    handleSubmit,
    getValues,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = methods

  useEffect(() => {
    if (!isSubmitting) {
      setIsSubmittingCurrent(false)
      setIsSubmittingNext(false)
    }
  }, [isSubmitting]);

  const { mutateAsync: updateStep } = useUpdateLastWizardStep()
  const { mutateAsync: submitForm } = useSaveStep2()

  const handlePostError = (data: any) => {
    Object.keys(data).forEach((x: any) => {
      if (x.toString().toLowerCase().startsWith('discounts[')) {
        setError(x.toLowerCase(), { message: data[x].join(' ') })
      }
    })
  }

  const {
    isOpen: isSavedFormAlertOpen,
    onOpen: savedFormAlertOnOpen,
    onClose: savedFormAlertOnClose,
  } = useDisclosure()

  const onSubmitForm = handleSubmit((data) => {
    let values = _.cloneDeep(data)
    // Datum poslu jen jako date bez casu. To by chtelo vyresit asi nejak obecne. Kdyz se to posle i s casem, tak dochazi k casovemu posunu, coz znamena na serveru predchozi den
    values.addons.vehicleFirstRegistrationDate = formatDate(values.addons.vehicleFirstRegistrationDate)

    return submitForm({ ...values, paramsId: paramsId ?? "" })
      .then((response) => {
        if (data.nextPage == 'next') {
          if ((insuranceParams?.generalParams.lastWizardStep ?? 2) < 3)
            updateStep({ paramsId: Number(paramsId), step: 3 })

          refetchInsuranceParams() // vždy refetch hodnot, jinak se na následujícím kroku zobrazují původní hodnoty 
          navigate(`/cestovko/wizard/3/${paramsId}`)
        } else {
          savedFormAlertOnOpen()
        }
      })
      .catch((err: AxiosError) => {
        if (!(err.response && err.response.status === 400 && err.response.data))
          return;

        let data = err.response.data
        handlePostError(data);
      })
      .finally(() => { })
  })

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmitForm}>
        <MainContent>
          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle>Připojištění</FormSectionTitle>

              <FormSectionDescription>
                Vyberte limity zvoleného připojišťění.
              </FormSectionDescription>
            </FormSectionHeader>

            <FormSectionBody>
              <ExtraInsuranceCestovko />
            </FormSectionBody>
          </FormSection>

          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle>Slevy pojištění</FormSectionTitle>

              <FormSectionDescription>
                Nastavte slevy pojistníka a jednotlivých pojišťoven.
              </FormSectionDescription>
            </FormSectionHeader>

            <FormSectionBody>
              <Discounts />
            </FormSectionBody>
          </FormSection>

          {!isContracted &&
            <FormButtons
              rightButtons={
                <>
                  <Button variant="outline"
                    disabled={isSubmitting || isSubmittingNext || isSubmittingCurrent}
                    isLoading={isSubmittingCurrent}
                    loadingText="Ukládám..."
                    type="button"
                    onClick={() => {
                      setValue('nextPage', 'current')
                      setIsSubmittingCurrent(true)
                      onSubmitForm()
                    }}>
                    Uložit
                  </Button>

                  <Button
                    colorScheme="primary"
                    isLoading={isSubmittingNext}
                    disabled={isSubmitting || isSubmittingNext || isSubmittingCurrent}
                    loadingText="Počítám nabídky..."
                    type="button"
                    onClick={() => {
                      setValue('nextPage', 'next')
                      setIsSubmittingNext(true)
                      onSubmitForm()
                    }}>
                    <Save />
                    Uložit a spočítat nabídky
                  </Button>

                  <SubmitDialog
                    isOpen={isSavedFormAlertOpen}
                    onClose={savedFormAlertOnClose}
                    header='Uloženo'
                    body={'Parametry nabídky byly úspěšně uloženy'}
                  />
                </>
              }
            />
          }
        </MainContent>

        <FormSpacer />

        <ErrorSummary errors={errors} />
      </form>
    </FormProvider>
  )
}
